import { HelpTip } from "@/components/HelpTip";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { tileSize } from "@/lib/dims";

const STATUSES = ["SELLABLE", "QUOTE_REQUIRED", "INACTIVE"];
const STATUS_BADGE = {
  SELLABLE: "badge-available",
  QUOTE_REQUIRED: "badge-reserved",
  INACTIVE: "badge-sold",
};
const STATUS_LABEL = {
  SELLABLE: "Available for sale",
  QUOTE_REQUIRED: "Quote required",
  INACTIVE: "Not available",
};

export default function AdminCatalog() {
  const [colors, setColors] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [flMap, setFlMap] = useState({});
  const [incomingMap, setIncomingMap] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/catalog/colors");
      setColors(data);
      // §17: operational availability alongside catalog rows
      api.get("/inventory?location=FLORIDA&canonical=1").then((res) => setFlMap(Object.fromEntries(res.data.map((r) => [r.sku, r.available])))).catch(() => {});
      api.get("/inventory/incoming-by-variant").then((res) => setIncomingMap(Object.fromEntries(res.data.map((r) => [r.sku, r])))).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const setColorStatus = async (code, status) => {
    try {
      await api.patch(`/admin/catalog/colors/${code}`, { status, note: "Admin catalog UI" });
      toast.success(`${code} → ${STATUS_LABEL[status]} — audited`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setQuoteMessage = async (code, quote_message) => {
    try {
      await api.patch(`/admin/catalog/colors/${code}`, { quote_message, note: "Admin catalog UI" });
      toast.success(`${code} quote message saved`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setColorName = async (code, name) => {
    try {
      await api.patch(`/admin/catalog/colors/${code}`, { name, note: "Admin catalog UI — display rename" });
      toast.success(`${code} display name updated — audited`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setVariantStatus = async (variantId, status) => {
    try {
      await api.patch(`/variants/${variantId}`, { status });
      toast.success("Variant status updated — audited");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!colors) return [];
    const q = search.trim().toUpperCase();
    return colors.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      return c.code.includes(q) || c.name.toUpperCase().includes(q)
        || (c.variants || []).some((v) => v.sku.includes(q));
    });
  }, [colors, search, statusFilter]);

  if (!colors) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-catalog-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  const totalVariants = colors.reduce((n, c) => n + (c.variants || []).length, 0);

  return (
    <div data-testid="admin-catalog-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Canonical catalog</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Colors &amp; variants</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        {colors.length} master colors · {totalVariants} variants. Status changes are audited and apply to new commercial activity only — historical orders, reservations and analytics are never rewritten.
      </p>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
        Numeric color codes (001–033) and canonical variant SKUs (MZ = 4×4 Zellige, BJ = 2×6 Bejmat, MD = 2×2 Zellige) are permanent identities. Display names are editable inline — a rename is audited and never creates a new product or rewrites history.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, name or SKU…" data-testid="catalog-search" className="h-11 w-72 bg-white focus-visible:ring-terracotta" />
        <a href="/admin/catalog/governance" data-testid="color-governance-link"
          className="inline-flex h-11 items-center rounded-md border border-terracotta px-4 text-sm font-medium text-terracotta transition-colors hover:bg-terracotta hover:text-white">
          Color Governance
        </a>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="catalog-status-filter" className="h-11 w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <HelpTip testid="help-quote-required" text="Quote Required items may be shown to dealers, but cannot receive normal automated pricing, order or reservation treatment — they require Zelligio review and a manual quotation (e.g. real silver or real gold special finishes)." />
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-border bg-white">
        <Table className="min-w-[860px]" data-testid="catalog-colors-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="w-8" />
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Code</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Color</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Quote message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <ColorRows key={c.code} color={c} expanded={!!expanded[c.code]}
                onToggle={() => setExpanded((x) => ({ ...x, [c.code]: !x[c.code] }))}
                onColorStatus={setColorStatus} onQuoteMessage={setQuoteMessage} onColorName={setColorName} onVariantStatus={setVariantStatus} flMap={flMap} incomingMap={incomingMap} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ColorRows({ color: c, expanded, onToggle, onColorStatus, onQuoteMessage, onColorName, onVariantStatus, flMap, incomingMap }) {
  return (
    <>
      <TableRow className="border-border" data-testid={`color-row-${c.code}`}>
        <TableCell className="py-3">
          <button onClick={onToggle} data-testid={`color-expand-${c.code}`} className="text-warmgrey hover:text-charcoal">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </TableCell>
        <TableCell className="py-3 font-mono text-xs text-charcoal">
          {c.code}
          {c.us_name_state === "PENDING_FOUNDER" && (
            <span className="ml-2 badge-status badge-reserved" data-testid={`us-name-pending-${c.code}`} title="U.S. color name awaiting Founder approval in Color Governance">
              U.S. name pending
            </span>
          )}
          {c.special_finish && (
            <span className="ml-2 badge-status badge-reserved" data-testid={`special-finish-${c.code}`}>
              <Sparkles className="mr-1 inline h-3 w-3" />{c.finish_descriptor}
            </span>
          )}
        </TableCell>
        <TableCell className="py-3">
          <Input defaultValue={c.name} key={`${c.code}-${c.name}`}
            onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== c.name && onColorName(c.code, e.target.value.trim())}
            data-testid={`color-name-${c.code}`} className="h-9 w-52 bg-white text-sm font-medium text-charcoal focus-visible:ring-terracotta" />
        </TableCell>
        <TableCell className="py-3">
          <Select value={c.status} onValueChange={(v) => onColorStatus(c.code, v)}>
            <SelectTrigger data-testid={`color-status-${c.code}`} className="h-9 w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
        </TableCell>
        <TableCell className="py-3">
          {c.status === "QUOTE_REQUIRED" ? (
            <Input defaultValue={c.quote_message || ""} onBlur={(e) => e.target.value !== (c.quote_message || "") && onQuoteMessage(c.code, e.target.value)}
              data-testid={`quote-message-${c.code}`} className="h-9 w-64 bg-white text-xs focus-visible:ring-terracotta" />
          ) : <span className="text-xs text-muted-foreground">—</span>}
        </TableCell>
      </TableRow>
      {expanded && (c.variants || []).map((v) => (
        <TableRow key={v.sku} className="border-border bg-background" data-testid={`variant-row-${v.sku}`}>
          <TableCell />
          <TableCell className="py-2 font-mono text-xs text-warmgrey">{v.sku}</TableCell>
          <TableCell className="py-2 text-xs text-muted-foreground">
            {tileSize(v) || v.format_in} · {v.pieces_per_box} pcs/box · {v.box_coverage_sqft} sqft/box
            <span className={`ml-2 badge-status ${STATUS_BADGE[v.effective_status]}`} data-testid={`variant-effective-${v.sku}`}>{STATUS_LABEL[v.effective_status]}</span>
            <span className="num ml-2 font-medium text-charcoal" data-testid={`variant-fl-${v.sku}`}>FL {flMap?.[v.sku] ?? 0}</span>
            {(incomingMap?.[v.sku]?.boxes ?? 0) > 0 && (
              <span className="ml-2 font-medium text-terracotta" data-testid={`variant-incoming-${v.sku}`}>+{incomingMap[v.sku].boxes} in{incomingMap[v.sku].eta ? ` · ETA ${incomingMap[v.sku].eta}` : ""}</span>
            )}
          </TableCell>
          <TableCell className="py-2">
            <Select value={v.status} onValueChange={(val) => onVariantStatus(v.id, val)}>
              <SelectTrigger data-testid={`variant-status-${v.sku}`} className="h-8 w-40 bg-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </TableCell>
          <TableCell />
        </TableRow>
      ))}
    </>
  );
}
